import { Button } from '@nextui-org/react';
import { ExternalLink } from 'lucide-react';
import { VendorLogo } from '../../../vendors-v2/components/VendorLogo';
import { VendorDetailRiskBadge } from '../atoms/VendorDetailRiskBadge';
import type { RiskLevel } from '../../../vendors-v2/types';

interface VendorDetailHeaderProps {
  vendorName: string;
  logoUrl: string | null;
  riskLevel: RiskLevel | null;
  websiteUrl: string | null;
  onNewAssessment?: () => void;
  className?: string;
}

export const VendorDetailHeader = ({
  vendorName,
  logoUrl,
  riskLevel,
  websiteUrl,
  onNewAssessment,
  className = '',
}: VendorDetailHeaderProps) => {
  return (
    <header
      className={`flex flex-row items-start gap-2 h-12 ${className}`}
      role="banner"
      aria-label={`Vendor details for ${vendorName}`}
    >
      {/* Left: Logo + Name + Risk + Website */}
      <div className="flex flex-col justify-center flex-1">
        <div className="flex flex-row items-start gap-[10px] h-12">
          {/* Logo */}
          <VendorLogo vendorName={vendorName} logoUrl={logoUrl} size={48} />

          {/* Name, Risk, Website */}
          <div className="flex flex-col justify-center gap-2 h-12">
            {/* Top Row: Name + Risk Badge */}
            <div className="flex flex-row items-center gap-2 h-6">
              <h1 className="font-['Inter'] font-semibold text-base leading-6 text-[#0F172B]">
                {vendorName}
              </h1>
              {riskLevel && <VendorDetailRiskBadge risk={riskLevel} />}
            </div>

            {/* Bottom Row: Website Link */}
            <div className="flex flex-row items-center gap-4 h-4">
              {websiteUrl && (
                <a
                  href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-['Inter'] font-normal text-xs leading-4 underline text-[#006FEE]"
                  aria-label={`Visit ${vendorName} website`}
                >
                  {websiteUrl.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: New Assessment Button */}
      {onNewAssessment && (
        <Button
          color="primary"
          onPress={onNewAssessment}
          className="flex flex-row items-center px-3 py-1 gap-[6px] h-7 bg-[#006FEE] rounded-[6px] text-white"
          aria-label={`Start new assessment for ${vendorName}`}
        >
          New Assessment
        </Button>
      )}
    </header>
  );
};
